import { DatePicker } from '@kula-ai/components';
import type {
  DatePickerTriggerProps,
  SingleDatePickerProps,
} from '@kula-ai/components';
import { format } from 'date-fns';
import { useDateFormat } from '@/hooks';
import { DATE_PANEL_CLASS, toDate } from './dateRangeValue';

/** What a trigger is given: kula's own props plus the formatted date. */
export type DateSingleTriggerProps = DatePickerTriggerProps & {
  /** "01/09/2026" in the account's format, or '' when unset. */
  dateLabel: string;
};

export type DateSinglePickerProps = {
  value?: string | Date | null;
  /** Fires on the day click, which is also when kula closes the panel. */
  onChange: (date: Date) => void;
  /** Required unless `isStatic`, which renders the grid with no trigger. */
  renderTrigger?: (props: DateSingleTriggerProps) => React.ReactNode;
  /** Render the calendar in flow rather than behind a trigger. */
  isStatic?: boolean;
  /** Grey out individual days; `minDate`/`maxDate` cover simple bounds. */
  disabledDay?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  /** Pass a dialog's own node when a focus trap would swallow a body portal. */
  portalTarget?: HTMLElement | null;
  /** Sunday by default, which is what every picker in the app rendered. */
  weekStartsOn?: 0 | 1;
  className?: string;
  calendarClassName?: string;
  /** Escape hatch onto react-date-range's Calendar; spreads last, so it wins. */
  calendarProps?: SingleDatePickerProps['calendarProps'];
};

/**
 * The app's single-date picker, sibling to `DateRangePicker`. Same split: this
 * draws the panel and formats the label, the caller owns form binding and any
 * per-surface rules. Selecting a day commits and closes — kula's single mode
 * has no confirm footer, which matches what these fields always did.
 *
 * `isStatic` drops the trigger and renders the grid inline, for the panels
 * that always show a calendar.
 */
export const DateSinglePicker = ({
  value,
  onChange,
  renderTrigger,
  isStatic = false,
  disabledDay,
  minDate,
  maxDate,
  portalTarget,
  weekStartsOn = 0,
  className,
  calendarClassName,
  calendarProps,
}: DateSinglePickerProps) => {
  const { dateFnsFormat } = useDateFormat();
  const selected = toDate(value);
  const dateLabel = selected ? format(selected, dateFnsFormat) : '';

  return (
    <DatePicker
      mode="single"
      value={selected ?? null}
      onChange={onChange}
      dateFormat={dateFnsFormat}
      isStatic={isStatic}
      // The panel short-circuits on a closed picker even when static, so a
      // static one has to be held open.
      {...(isStatic ? { open: true } : {})}
      portalTarget={portalTarget}
      className={className}
      calendarClassName={[DATE_PANEL_CLASS, calendarClassName]
        .filter(Boolean)
        .join(' ')}
      calendarProps={{
        // kula's panel hardcodes Monday over its own locale, and the spread
        // lands after that literal, so this is what takes effect.
        weekStartsOn,
        ...(minDate ? { minDate } : {}),
        ...(maxDate ? { maxDate } : {}),
        ...(disabledDay ? { disabledDay } : {}),
        ...calendarProps,
      }}
      renderTrigger={(props) =>
        isStatic ? null : renderTrigger?.({ ...props, dateLabel })
      }
    />
  );
};
